'use client'

import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

// Hooks
import {
  useComponentVisible,
  useResponsive,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { useFavorites } from '@dustin-web/product-components/src/hooks/dataloader/use-favorites'

// Components
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import { NumberBadge } from '@dustin-web/microsite-components/src/badge'
import { Drawer } from '@dustin-web/microsite-components/src/drawer'
import { FilterBox } from '@dustin-web/product-components/src/filters/filter-box'
import Link from 'next/link'
import HeartIcon from '@dustin-web/microsite-components/src/icon/generated/heart'
import Tooltip from '@dustin-web/microsite-components/src/tooltip'
import { DropdownItem, DropdownList } from './components/dropdown'

const PAGE_URL = '/favorites'

const Container = () => {
  const { isSmallScreen } = useResponsive()
  const { translate } = useTranslations()
  const { favoritesLists, error } = useFavorites() // use same as in cartsite to keep same cache
  const [itemQuantity, setItemQuantity] = useState<number | undefined>(0)
  const [showDrawer, setShowDrawer] = useState(false)
  const { componentRef, isVisible, setIsVisible } = useComponentVisible<HTMLDivElement>(false)

  const toggleIsVisible = () => setIsVisible(!isVisible)
  const toggleDrawer = () => {
    setShowDrawer(!showDrawer)
  }

  const showBadge = !!itemQuantity && itemQuantity > 0 && !isVisible
  const sortedList = favoritesLists?.sort((a: any, b: any) =>
    a.productListName ? a.productListName.localeCompare(b.productListName ?? '') : 0
  )

  useEffect(() => {
    setItemQuantity(
      favoritesLists?.reduce((prev, cur) => prev + (cur?.items?.length ? cur.items.length : 0), 0)
    )
  }, [setItemQuantity, favoritesLists])

  if (error) {
    throw error // TODO: not sure why we throw this error like this
  }

  return (
    <FavoritesMenu
      showBadge={showBadge}
      toggleDrawer={toggleDrawer}
      sortedList={sortedList}
      isSmallScreen={isSmallScreen}
      translate={translate}
      itemQuantity={itemQuantity}
      showDrawer={showDrawer}
      componentRef={componentRef}
      isVisible={isVisible}
      toggleIsVisible={toggleIsVisible}
    />
  )
}

type FavoritesMenuProps = {
  showBadge: boolean
  toggleDrawer: () => void
  isSmallScreen: boolean
  translate: (arg: string) => string
  sortedList: any
  itemQuantity: number | undefined
  showDrawer: boolean
  componentRef: React.RefObject<(HTMLDivElement & HTMLElement) | null>
  isVisible: boolean
  toggleIsVisible: () => void
}
const FavoritesMenu = ({
  showBadge,
  toggleDrawer,
  isSmallScreen,
  translate,
  sortedList,
  itemQuantity,
  showDrawer,
  componentRef,
  isVisible,
  toggleIsVisible,
}: FavoritesMenuProps) => (
  <ErrorBoundary>
    {isSmallScreen ? (
      <Mobile
        toggleDrawer={toggleDrawer}
        showBadge={showBadge}
        showDrawer={showDrawer}
        favoritesLists={sortedList}
        listTitle={translate('Favorites_Lists')}
      />
    ) : (
      <Desktop
        isVisible={isVisible}
        toggleIsVisible={toggleIsVisible}
        showBadge={showBadge}
        itemQuantity={itemQuantity}
        favoritesLists={sortedList}
        componentRef={componentRef}
        emptyText={translate('Favorites_Empty')}
        title={translate('MyFavorites')}
      />
    )}
  </ErrorBoundary>
)

type DesktopProps = {
  isVisible: boolean
  toggleIsVisible: () => void
  showBadge: boolean
  itemQuantity: any
  favoritesLists: any
  componentRef: React.RefObject<(HTMLDivElement & HTMLElement) | null>
  emptyText: string
  title: string
}
const Desktop = ({
  isVisible,
  toggleIsVisible,
  showBadge,
  itemQuantity,
  favoritesLists,
  componentRef,
  emptyText,
  title,
}: DesktopProps) => (
  <li
    className={classNames('c-header__user-item c-header__dropdown u-pr-24 u-line-height-1', {
      'c-header__dropdown--active': isVisible,
    })}>
    <button
      data-ga-title="favorites"
      id="header-favorites-btn"
      className="c-header__user-btn c-header__dropdown-btn button-stripped"
      aria-label={title}
      onClick={() => toggleIsVisible()}
      aria-expanded={isVisible}>
      <Tooltip popoverLayerClassName="c-header__user-btn--tooltip" tooltipText={title}>
        <span className="c-header__user-icon only">
          <HeartIcon name="heart" />
        </span>
      </Tooltip>
      <NumberBadge
        show={showBadge}
        quantity={itemQuantity || 0}
        maxQuantity={9}
        className="c-header__badge icon-only u-bg-gray-08 u-color-white"
        data-test-id="favorites-count"
      />
    </button>
    <div
      className="c-header__dropdown-box"
      ref={componentRef}
      aria-labelledby="header-favorites-btn">
      {favoritesLists.length > 0 ? (
        <DropdownList>
          {favoritesLists.map((list: any, i: number) => (
            <DropdownItem
              key={i}
              linkHref={`/favorites?listId=${list?.id}`}
              linkRel="nofollow"
              hasArrowIcon
              quantity={list?.items?.length}
              title={list?.productListName}
            />
          ))}
        </DropdownList>
      ) : (
        <div className="u-py-8 u-px-8">{emptyText}</div>
      )}
    </div>
  </li>
)

type MobileProps = {
  toggleDrawer: () => void
  showBadge: boolean
  showDrawer: boolean
  favoritesLists: any
  listTitle: string
}

const Mobile = ({
  toggleDrawer,
  showBadge,
  showDrawer,
  favoritesLists,
  listTitle,
}: MobileProps) => (
  <>
    <a
      data-ga-title="favorites"
      aria-label="favorites"
      tabIndex={0}
      role="button"
      className="c-mobile-header-menu__secondary-action u-mr-16 u-relative d-block"
      onClick={toggleDrawer}>
      <span className="d-flex align-items-center" data-test-id="favorites-count">
        <HeartIcon name="heart" className="i-24" />
      </span>
      <NumberBadge
        show={showBadge}
        maxQuantity={9}
        className="u-bg-red u-absolute u-radius-4 c-mobile-header-menu__secondary-action__badge"
        data-test-id="favorites-count"
      />
    </a>
    <Drawer
      visible={showDrawer}
      onClose={toggleDrawer}
      className={`u-px-0 u-radius-0 u-dustin-scrollbar u-color-black`}
      position="right"
      theme="white">
      <FilterBox title={listTitle} icon="heart" withPadding>
        <ul className="ul-stripped u-mb-0 u-fs-14">
          {favoritesLists.map((list: any, key: number) => (
            <Link
              prefetch={false}
              key={list.productListName + key}
              href={`${PAGE_URL}?listId=${list.id}`}
              onClick={toggleDrawer}
              className="a-stripped d-block justify-content-start u-overflow-ellipsis u-w-100 u-mr-4 u-mb-8">
              {`${list.productListName} (${list.items.length})`}
            </Link>
          ))}
        </ul>
      </FilterBox>
    </Drawer>
  </>
)

export default Container
